import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, CheckCircle2, ClipboardCopy, Download, Loader2, RotateCcw, RotateCw, Search, Send } from 'lucide-react';
import { toast } from 'sonner';
import API from '../../utils/apiBase';
import PeopleWorkspaceShell from '../../components/people/PeopleWorkspaceShell';
import { safeWorkforceSlug } from '../../lib/staffIntroductionInvitation';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const AUTOMATIC_PACK_CONTROLS = [
  'identity', 'right_to_work', 'dbs', 'proof_of_address',
  'references', 'mandatory_training', 'induction', 'professional_registration',
];
// The exported headings are selected from current saved records, NOT from
// manager checkboxes. Selecting a heading never changes employer verification.
const includedStaffSections = (preview) => {
  const controls = preview?.controls || {};
  const included = AUTOMATIC_PACK_CONTROLS.filter(key => {
    const row = controls[key];
    return row && row.applicable !== false && (row.verified || Number(row.file_count) > 0);
  });
  return included.length ? included : ['identity', 'right_to_work', 'dbs', 'proof_of_address'];
};

const statusLabel = {
  not_requested: 'Not requested',
  requested: 'Awaiting staff',
  pending_review: 'Needs manager review',
  changes_requested: 'Corrections requested',
  approved: 'Approved',
  withdrawn: 'Withdrawn',
};

export default function StaffIntroductionsPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState('');
  const [search, setSearch] = useState('');
  const [clientOrgId, setClientOrgId] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteExpires, setInviteExpires] = useState('');
  const [clients, setClients] = useState([]);
  const [note, setNote] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [selectedForPack, setSelectedForPack] = useState([]);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [managerSummary, setManagerSummary] = useState('');
  const [selectedSections, setSelectedSections] = useState(AUTOMATIC_PACK_CONTROLS);
  const [includePhoto, setIncludePhoto] = useState(true);
  const [includeBio, setIncludeBio] = useState(true);
  const [includeInternalChecklist, setIncludeInternalChecklist] = useState(false);
  const [bioNeedsSave, setBioNeedsSave] = useState(false);
  const [bioSource, setBioSource] = useState('manager_written');
  const [reviewedSources, setReviewedSources] = useState(false);
  const [summaryApproved, setSummaryApproved] = useState(false);
  const [photoAuthorised, setPhotoAuthorised] = useState(false);
  const [releaseToClient, setReleaseToClient] = useState(false);
  const [documentRegisterForClient, setDocumentRegisterForClient] = useState(false);
  const [namedRecipient, setNamedRecipient] = useState('');
  const blobRef = useRef(null);
  const current = items.find(item => item.employee_id === selected) || null;
  // One-use link is kept in manager memory only and never re-fetched from the server.
  const link = inviteLink;

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff-introductions`, { headers: auth(), timeout: 20000 });
      const rows = Array.isArray(data?.items) ? data.items : [];
      // Staff awaiting agency review belongs first, not buried in an A–Z roster.
      setItems(rows.sort((a, b) => (
        Number(b.status === 'pending_review') - Number(a.status === 'pending_review')
        || (b.submitted_at || '').localeCompare(a.submitted_at || '')
        || a.name.localeCompare(b.name)
      )));
      setSelected(old => old || rows[0]?.employee_id || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load staff profiles.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/organisations`, { headers: auth(), timeout: 20000 })
      .then(({ data }) => {
        if (!active) return;
        setClients((data?.organisations || []).filter(org => (
          org?.id && org.is_active === true && org.relationship_stage === 'active_client'
        )));
      })
      .catch(() => toast.error('Could not load active clients. Profile requests remain disabled.'));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setEvidencePreview(null);
    setManagerSummary('');
    setSelectedSections(AUTOMATIC_PACK_CONTROLS);
    setBioNeedsSave(false);
    setBioSource('manager_written');
    setReviewedSources(false);
    setSummaryApproved(false);
    setPhotoAuthorised(false);
    setReleaseToClient(false);
    setDocumentRegisterForClient(false);
    if (!selected || !clientOrgId) return () => { active = false; };
    setPreviewLoading(true);
    axios.get(`${API}/staff-introductions/${encodeURIComponent(selected)}/assurance-preview`, {
      headers: auth(), timeout: 60000, params: { organisation_id: clientOrgId },
    }).then(({ data }) => {
      if (!active) return;
      setEvidencePreview(data);
      setSelectedSections(includedStaffSections(data));
      setManagerSummary(data.existing_professional_summary || '');
      setBioSource(data.bio_source || 'manager_written');
      setBioNeedsSave(false);
    }).catch((error) => {
      if (active) toast.error(error.response?.data?.detail || 'Could not check the current staff evidence.');
    }).finally(() => { if (active) setPreviewLoading(false); });
    return () => { active = false; };
  }, [selected, clientOrgId]);

  useEffect(() => {
    if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    setPhotoUrl('');
    if (!current?.has_photo) return;
    let active = true;
    axios.get(`${API}/staff-introductions/${encodeURIComponent(selected)}/photo`, {
      headers: auth(), responseType: 'blob',
      params: { approved: current.status === 'approved' },
    }).then(({ data }) => {
      if (!active) return;
      const url = URL.createObjectURL(data);
      blobRef.current = url;
      setPhotoUrl(url);
    }).catch(() => { /* Private preview unavailable, no fallback to public storage. */ });
    return () => {
      active = false;
      if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    };
  }, [selected, current?.submitted_at, current?.has_photo, current?.status, photoVersion]);

  const post = async (suffix, body, action) => {
    if (!selected || busy) return;
    setBusy(action);
    try {
      const { data } = await axios.post(`${API}/staff-introductions/${encodeURIComponent(selected)}/${suffix}`, body, {
        headers: auth(), timeout: 20000,
      });
      if (action === 'request' && data?.invite_token) {
        const slug = safeWorkforceSlug(data.workforce_slug);
        if (!slug) {
          setInviteLink('');
          toast.error('Invitation saved, but the agency workforce portal needs configuration. Contact your administrator.');
        } else {
          // Use the exact origin which the worker magic-login email uses:
          // other origins cannot share the short-lived browser continuation.
          const loginOrigin = new URL(data.frontend_url || window.location.origin);
          const localDev = loginOrigin.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(loginOrigin.hostname);
          if ((!localDev && loginOrigin.protocol !== 'https:') || loginOrigin.username || loginOrigin.password) {
            throw new Error('Invalid workforce portal origin');
          }
          const base = `${loginOrigin.origin}/worker/staff-introduction`;
          setInviteLink(`${base}?workspace=${encodeURIComponent(slug)}#invite=${encodeURIComponent(data.invite_token)}`);
          setInviteExpires(data.expires_at || '');
          toast.success('Private invitation created. Copy it securely now.');
        }
      } else {
        if (action === 'request') setInviteLink('');
        toast.success(action === 'request' ? 'Staff profile request saved' : 'Review saved');
      }
      setNote('');
      await refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'The action could not be completed.');
    } finally { setBusy(''); }
  };

  const request = () => post('request', { organisation_id: clientOrgId }, 'request');
  const review = (decision) => post('review', { decision, reason: note.trim() }, decision);

  const copy = async () => {
    if (!inviteLink) { toast.error('Create a new invitation first.'); return; }
    try {
      await navigator.clipboard.writeText(link);
      toast.success('One-use invitation copied. The correct worker must sign in first.');
    } catch { toast.error('Could not copy the link.'); }
  };

  const rotatePhoto = async (direction) => {
    if (!current || !['pending_review', 'approved'].includes(current.status) || busy) return;
    setBusy('rotate');
    try {
      await axios.post(`${API}/staff-introductions/${encodeURIComponent(selected)}/photo/rotate`,
        { direction }, { headers: auth(), timeout: 60000 });
      setPhotoVersion(value => value + 1);
      await refresh();
      toast.success(current.status === 'approved'
        ? 'Portrait corrected. A new approved PDF version is available.'
        : 'Private portrait rotated. Review the preview before approving.');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail : 'Could not rotate the private portrait.');
    } finally { setBusy(''); }
  };

  const download = async () => {
    if (!current || busy) return;
    setBusy('pdf');
    try {
      const { data } = await axios.get(`${API}/staff-introductions/${encodeURIComponent(selected)}/pdf`, {
        headers: auth(), responseType: 'blob', timeout: 35000,
      });
      const fileUrl = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = fileUrl;
      anchor.download = `Staff_Profile_${current.name.replace(/[^a-z0-9]+/gi, '_')}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Some browsers cancel a just-started download if the blob is revoked
      // synchronously. Release it after the browser has opened the file.
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 30_000);
      toast.success('Profile PDF downloaded. Check your browser Downloads folder.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'The approved PDF could not be generated.');
    } finally { setBusy(''); }
  };

  const togglePackStaff = (id) => {
    if (!selectedForPack.includes(id) && selectedForPack.length >= 12) {
      toast.error('Generate up to 12 individual staff PDFs per batch.');
      return;
    }
    setSelectedForPack(old => old.includes(id)
      ? old.filter(item => item !== id)
      : [...old, id]);
    // A different selected worker invalidates any previous individual release
    // sign-off. Focus the worker so the evidence preview matches the export.
    setSelected(id);
    setReviewedSources(false);
    setSummaryApproved(false);
    setPhotoAuthorised(false);
    setReleaseToClient(false);
    setDocumentRegisterForClient(false);
    setNamedRecipient('');
  };

  const createBioDraft = async () => {
    if (!selected || busy) return;
    setBusy('bio-draft');
    try {
      const { data } = await axios.get(
        `${API}/staff-introductions/${encodeURIComponent(selected)}/bio-draft`,
        { headers: auth(), timeout: 20000 },
      );
      if (!data?.source_available || !data?.draft) {
        toast.info(data?.message || 'No submitted interview answers are available. Write a factual bio instead.');
        return;
      }
      setManagerSummary(data.draft);
      setBioSource('interview_draft');
      setBioNeedsSave(true);
      toast.info('Interview-based draft prepared. Check it against the answers, edit, then save.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not use the saved interview answers.');
    } finally { setBusy(''); }
  };

  const saveStaffBio = async () => {
    if (!selected || busy) return;
    if (managerSummary.trim().length < 30) {
      toast.error('Write at least 30 characters of factual staff information.');
      return;
    }
    setBusy('bio-save');
    try {
      const { data } = await axios.put(
        `${API}/staff-introductions/${encodeURIComponent(selected)}/bio`,
        { summary: managerSummary.trim(), source: bioSource, reviewed: true },
        { headers: auth(), timeout: 20000 },
      );
      setManagerSummary(data.summary);
      setBioNeedsSave(false);
      setEvidencePreview(prev => prev?.employee_id === selected
        ? { ...prev, existing_professional_summary: data.summary, bio_saved: true }
        : prev);
      toast.success('Approved bio saved to the staff profile. No invitation or email was sent.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the staff biography.');
    } finally { setBusy(''); }
  };

  const exportAssurancePacks = async () => {
    if (!clientOrgId || busy) return;
    const ids = selectedForPack.length ? selectedForPack : (selected ? [selected] : []);
    if (!ids.length) {
      toast.error('Choose a staff member first.');
      return;
    }
    if (includeBio && bioNeedsSave && managerSummary.trim()) {
      toast.error('Review and save this biography before exporting. It is still an unsaved draft.');
      return;
    }
    const clientCopy = releaseToClient || documentRegisterForClient;
    if (clientCopy && ids.length !== 1) {
      toast.error('Review and authorise each external staff profile individually.');
      return;
    }
    // A checkbox selection and an evidence preview must refer to the SAME
    // person. Never use one worker's manager attestations for another worker.
    if (clientCopy && (
      ids[0] !== selected || previewLoading
      || !evidencePreview || evidencePreview.employee_id !== ids[0]
    )) {
      toast.error('Open the selected staff member and review their current evidence before releasing.');
      return;
    }
    // DISPLAY choices only. Every staff member's evidence and verification
    // are still independently loaded from the tenant-scoped backend.
    const selectedControls = AUTOMATIC_PACK_CONTROLS.filter(key => selectedSections.includes(key));
    if (!selectedControls.length && !includePhoto && !includeBio) {
      toast.error('Choose at least one PDF section, photograph or biography.');
      return;
    }
    if (releaseToClient && (!includePhoto || !includeBio || !selectedControls.length)) {
      toast.error('Verified assurance needs a photograph, biography and at least one evidence section.');
      return;
    }
    setBusy('assurance');
    try {
      const { data, headers } = await axios.post(`${API}/staff-introductions/assurance/export`, {
        organisation_id: clientOrgId, employee_ids: ids,
        selected_controls: selectedControls,
        include_internal_checklist: includeInternalChecklist && !clientCopy,
        include_photo: includePhoto,
        include_biography: includeBio,
        release_to_client: releaseToClient,
        document_register_for_client: documentRegisterForClient,
        recipient_name: namedRecipient.trim(),
        reviewed_source_records: reviewedSources,
        professional_summary_approved: summaryApproved,
        authorised_photo_use: photoAuthorised,
        summaries: includeBio && selected && managerSummary.trim() ? { [selected]: managerSummary.trim() } : {},
      }, { headers: auth(), responseType: 'blob', timeout: 180000 });
      const url = URL.createObjectURL(data);
      const isZip = ids.length > 1;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = isZip ? 'Staff_Profiles.zip'
        : `Staff_Assurance_${(current?.name || 'Employee').replace(/[^a-z0-9]+/gi, '_')}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(releaseToClient
        ? 'Verified assurance PDF generated. Deliver only through an authorised secure channel.'
        : documentRegisterForClient
          ? 'Factual staff file register generated. It is NOT evidence of work clearance. Share only with the named authorised recipient.'
          : 'Internal review pack saved to Downloads. Not authorised for external release.');
    } catch (error) {
      let message = 'Could not generate the assurance pack.';
      if (error.response?.data instanceof Blob) {
        try { message = (await error.response.data.text() ? JSON.parse(await error.response.data.text()).detail : message) || message; }
        catch { /* keep generic error */ }
      } else if (typeof error.response?.data?.detail === 'string') message = error.response.data.detail;
      toast.error(message);
    } finally { setBusy(''); }
  };

  const visible = items.filter(row => (
    `${row.name} ${row.role} ${row.requested_for}`.toLowerCase().includes(search.toLowerCase())
  ));

  return (
    <PeopleWorkspaceShell
      activeView="staff_profiles"
      title="Staff profiles"
      description="Secure one-page staff introductions for care homes, separate from employment and compliance evidence."
      actions={
        <button type="button" onClick={refresh} className="inline-flex items-center gap-1 rounded-lg border bg-white px-3 py-2 text-sm">
          <RotateCcw size={16} /> Refresh
        </button>
      }
    >
      <section className="mx-auto max-w-7xl space-y-5 px-4 pb-6 text-slate-800 md:px-6">
      <section className="rounded-2xl border border-blue-200 bg-white p-5 shadow-sm" data-testid="admin-staff-assurance-generator">
        <h2 className="text-lg font-bold text-slate-900">Generate a staff profile</h2>
        <p className="mt-1 text-sm text-slate-600">
          Tick the sections you want included. The original records and verification results will stay unchanged.
          Select multiple staff only when you want individual PDFs in a private ZIP.
        </p>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div>
            <label htmlFor="pack-client" className="text-sm font-semibold">Prepare profile for</label>
            <select id="pack-client" value={clientOrgId} onChange={e => setClientOrgId(e.target.value)}
              className="mt-1 block w-full rounded-lg border px-3 py-2.5 text-sm">
              <option value="">Choose a care home</option>
              {clients.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Names come from your recorded active-client list. Choosing a name here does not confirm an agreed placement, booking or contract.
            </p>
            <div className="mt-4 rounded-xl border p-3" data-testid="staff-profile-section-selector">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-bold">Choose what appears in the PDF</h3>
                <div className="flex gap-3 text-xs">
                  <button type="button" className="font-semibold text-blue-700 underline"
                    onClick={() => { setSelectedSections(AUTOMATIC_PACK_CONTROLS); setIncludePhoto(true); setIncludeBio(true); }}>Select all</button>
                  <button type="button" className="font-semibold text-slate-700 underline"
                    onClick={() => { setSelectedSections([]); setIncludePhoto(false); setIncludeBio(false); }}>Clear all</button>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <label className="flex items-start gap-2 text-xs" htmlFor="staff-pack-include-photo">
                  <input id="staff-pack-include-photo" type="checkbox" checked={includePhoto}
                    onChange={e => setIncludePhoto(e.target.checked)} /> <span>Staff photograph</span>
                </label>
                <label className="flex items-start gap-2 text-xs" htmlFor="staff-pack-include-bio">
                  <input id="staff-pack-include-bio" type="checkbox" checked={includeBio}
                    onChange={e => setIncludeBio(e.target.checked)} /> <span>About the staff member (bio)</span>
                </label>
                {AUTOMATIC_PACK_CONTROLS.map(key => (
                  <label key={key} className="flex items-start gap-2 text-xs" htmlFor={`staff-pack-section-${key}`}>
                    <input id={`staff-pack-section-${key}`} type="checkbox"
                      checked={selectedSections.includes(key)}
                      onChange={e => setSelectedSections(old => e.target.checked
                        ? [...new Set([...old, key])] : old.filter(item => item !== key))} />
                    <span>{evidencePreview?.controls?.[key]?.label || key.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
              <label className="mt-3 flex items-start gap-2 border-t pt-3 text-xs" htmlFor="staff-pack-internal-checklist">
                <input id="staff-pack-internal-checklist" type="checkbox" checked={includeInternalChecklist}
                  onChange={e => setIncludeInternalChecklist(e.target.checked)}
                  disabled={releaseToClient || documentRegisterForClient} />
                <span>Extra manager checklist (private internal exports only)</span>
              </label>
              <p className="mt-2 text-xs text-slate-500">
                Unticking hides a section from this PDF only. Mandatory training counts role-required courses, not uploaded PDF files or Care Certificate standards. Induction is a separate section. Longer profiles continue on another A4 page. Verification dates are not printed.
              </p>
            </div>
            <label htmlFor="pack-summary" className="mt-4 block text-sm font-semibold">
              About this staff member
            </label>
            <textarea id="pack-summary" rows={4} maxLength={950} value={managerSummary}
              onChange={e => {
                setManagerSummary(e.target.value);
                setBioNeedsSave(true);
              }}
              className="mt-1 w-full rounded-xl border p-3 text-sm"
              placeholder="Write a short, factual introduction. You can also use their interview answers to start a draft." />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" onClick={createBioDraft} disabled={!!busy || !selected}
                data-testid="staff-bio-interview-draft"
                className="rounded-lg border border-blue-300 px-3 py-2 text-xs font-semibold text-blue-800 disabled:opacity-50">
                Create draft from interview
              </button>
              <button type="button" onClick={saveStaffBio}
                disabled={!!busy || !selected || !bioNeedsSave || managerSummary.trim().length < 30}
                data-testid="staff-bio-save"
                className="rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
                Save reviewed bio
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {bioNeedsSave
                ? 'Draft only — check the facts and save before it appears in the PDF.'
                : managerSummary ? 'Saved wording will be reused for this person.' : 'No bio saved yet. The PDF will not show a missing-bio warning.'}
              No medical, immigration or private contact information.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-bold">Saved documents and completed checks</h3>
            {previewLoading && <p className="text-xs text-blue-700">Checking saved records…</p>}
            {evidencePreview && !previewLoading ? (
              <div className="mt-2 space-y-1 rounded-xl border p-3 text-xs" data-testid="staff-profile-source-preview">
                <p className="font-semibold">{evidencePreview.name} · {evidencePreview.role?.replace(/_/g, ' ')}</p>
                <p>Photograph: {evidencePreview.photo_available ? 'Recorded — confirm permission before external sharing' : 'Not recorded'}</p>
                <p>Approved introduction for this care home: {evidencePreview.approved_introduction ? 'Yes' : 'Not yet'}</p>
                {Object.entries(evidencePreview.controls || {})
                  .filter(([key]) => selectedSections.includes(key))
                  .map(([key, row]) => (
                    <div key={key} className="flex justify-between gap-2 border-t py-1">
                      <span>{row.label}</span>
                      <span className="text-right">
                        <span className={row.file_count ? 'block text-blue-800' : 'block text-slate-500'}>{row.file_status}</span>
                        <span className={row.verified ? 'block font-semibold text-emerald-700' : 'block font-semibold text-amber-800'}>{row.status}</span>
                      </span>
                    </div>
                  ))}
                <p className="pt-1 text-slate-600">Only selected sections print. Missing or pending evidence remains clearly labelled; section selection cannot verify a check.</p>
              </div>
            ) : <p className="mt-2 text-xs text-slate-500">Choose a care home and staff member to view the saved records.</p>}
          </div>
        </div>
        <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="staff-profile-external-sharing-options">
          <summary className="cursor-pointer font-semibold text-slate-800">Need a version for a care home? (Extra authorisation)</summary>
          <p className="mt-2 text-xs text-slate-600">
            Internal profiles stay private. Client copies require one named staff member, an authorised recipient and a manager's review.
          </p>
          <div className="mt-3 space-y-2">
            <label className="flex gap-2"><input type="radio" name="staff-pack-release-mode"
              checked={!documentRegisterForClient && !releaseToClient}
              onChange={() => { setReleaseToClient(false); setDocumentRegisterForClient(false); }} />
              Internal profile only (default)</label>
            <label className="flex gap-2"><input type="radio" name="staff-pack-release-mode"
              checked={documentRegisterForClient}
              onChange={() => { setDocumentRegisterForClient(true); setReleaseToClient(false); }} />
              Factual staff document register — NOT work clearance</label>
            <label className="flex gap-2"><input type="radio" name="staff-pack-release-mode"
              checked={releaseToClient}
              onChange={() => { setReleaseToClient(true); setDocumentRegisterForClient(false); }} />
              Verified client assurance — only when all required checks are recorded</label>
          </div>
          {(releaseToClient || documentRegisterForClient) && (
            <div className="mt-3 space-y-2 border-t pt-3">
              <p className="text-xs font-semibold">Confirm before sharing externally</p>
              <label className="flex gap-2"><input type="checkbox" checked={reviewedSources} onChange={e => setReviewedSources(e.target.checked)} />
                I have checked the original records and any work restrictions.</label>
              <label className="flex gap-2"><input type="checkbox" checked={summaryApproved} onChange={e => setSummaryApproved(e.target.checked)} />
                The professional introduction is accurate for this care home.</label>
              <label className="flex gap-2"><input type="checkbox" checked={photoAuthorised} onChange={e => setPhotoAuthorised(e.target.checked)} />
                The staff photograph is authorised for this recipient.</label>
              <label htmlFor="pack-recipient" className="block text-xs font-semibold">
                Named authorised recipient
                <input id="pack-recipient" value={namedRecipient} maxLength={100}
                  onChange={e => setNamedRecipient(e.target.value)}
                  className="mt-1 block w-full rounded-lg border bg-white px-3 py-2.5 text-sm"
                  placeholder="Care-home manager's full name" />
              </label>
              <p className="text-xs text-slate-600">
                Factual registers must never be treated as certified RTW, DBS or training clearance.
                Staff work restrictions and full evidence stay in restricted CareDock records; they are not printed in the care-home profile.
              </p>
            </div>
          )}
        </details>
        <button type="button" onClick={exportAssurancePacks}
          disabled={!!busy || !clientOrgId || (!selected && !selectedForPack.length)}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-bold text-white disabled:opacity-50">
          <Download size={18} /> {busy === 'assurance' ? 'Generating…' : selectedForPack.length > 1 ? 'Download internal staff ZIP' : documentRegisterForClient ? 'Generate factual staff profile' : releaseToClient ? 'Generate verified assurance PDF' : 'Download staff profile PDF'}
        </button>
        <p className="mt-2 text-xs text-slate-500">
          CareDock only downloads this document for the signed-in manager. The export does not mark missing paper evidence as uploaded or approve outstanding checks.
        </p>
      </section>
      <div className="grid gap-5 lg:grid-cols-[310px_minmax(0,1fr)]">
        <aside className="rounded-2xl border bg-white p-3">
          <label className="flex items-center gap-2 rounded-lg border px-3 py-2 text-slate-500">
            <Search size={17} />
            <input aria-label="Search staff" className="min-w-0 flex-1 outline-none" placeholder="Find a staff member"
              value={search} onChange={e => setSearch(e.target.value)} />
          </label>
          <p className="px-2 pt-3 text-xs text-slate-500">{visible.length} staff records · {items.filter(item => item.status === 'pending_review').length} awaiting manager review</p>
          <p className="px-2 pt-1 text-xs text-slate-500">Profiles awaiting review appear first. Search by staff name.</p>
          <div className="mt-2 flex flex-wrap gap-2 px-1">
            <button type="button" onClick={() => setSelectedForPack(visible.slice(0,12).map(item => item.employee_id))}
              className="rounded-lg border px-2.5 py-1.5 text-xs font-semibold">Select first 12 visible</button>
            <button type="button" onClick={() => setSelectedForPack([])}
              className="rounded-lg border px-2.5 py-1.5 text-xs">Clear selection</button>
          </div>
          <div className="mt-2 max-h-[560px] space-y-1 overflow-y-auto">
            {loading && <div className="flex items-center gap-2 p-3"><Loader2 className="animate-spin" /> Loading…</div>}
            {visible.map(item => (
              <div key={item.employee_id} className={`flex items-start gap-2 rounded-xl border px-2 py-2 ${selected === item.employee_id ? 'border-blue-300 bg-blue-50' : 'border-transparent hover:bg-slate-50'}`}>
                <label className="mt-2" title="Add this person to a PDF export">
                  <input type="checkbox" aria-label={`Include ${item.name} in staff pack`}
                    checked={selectedForPack.includes(item.employee_id)}
                    onChange={() => togglePackStaff(item.employee_id)} />
                </label>
                <button type="button"
                  onClick={() => { setSelected(item.employee_id); setClientOrgId(old => old || item.client_org_id || ''); setInviteLink(''); setInviteExpires(''); }}
                  className="min-w-0 flex-1 p-1 text-left">
                  <span className="block truncate font-semibold">{item.name}</span>
                  <span className="block truncate text-xs text-slate-500">{item.role}</span>
                  <span className="mt-1 block text-xs text-blue-700">{statusLabel[item.status] || item.status}</span>
                </button>
              </div>
            ))}
          </div>
        </aside>
        <article className="rounded-2xl border bg-white p-5 shadow-sm md:p-7">
          {!current ? <p className="text-slate-600">Select a staff member to start.</p> : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-5">
                {photoUrl ? (
                  <img src={photoUrl} alt={`Portrait submitted by ${current.name}`} className="h-32 w-28 rounded-lg border bg-slate-50 object-contain" />
                ) : (
                  <div className="flex h-32 w-28 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><Camera size={34} /></div>
                )}
                <div>
                  <h2 className="text-xl font-bold">{current.name}</h2>
                  <p className="text-sm text-slate-500">{current.role}</p>
                  <p className="mt-2 text-sm font-semibold text-blue-700">{statusLabel[current.status] || current.status}</p>
                  {current.approved_at && <p className="mt-1 text-xs text-slate-500">Last approved: {current.approved_at.slice(0, 10)}</p>}
                  {current.status === 'pending_review' && (
                    <p className="mt-2 max-w-sm rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      Submission received. Review the photo and statement below, then approve. The PDF download will appear here after approval.
                    </p>
                  )}
                  {['requested', 'changes_requested'].includes(current.status) && (
                    <p className="mt-2 max-w-sm text-xs text-slate-600">
                      No approved PDF yet. The staff member must finish or correct their submission before it can be reviewed.
                    </p>
                  )}
                  {['pending_review', 'approved'].includes(current.status) && current.has_photo && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-600">Photo sideways? Rotate it without uploading again. Approved profiles receive a new, auditable PDF version.</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" onClick={() => rotatePhoto('left')} disabled={!!busy}
                          className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs disabled:opacity-50">
                          <RotateCcw size={14} /> Rotate left
                        </button>
                        <button type="button" onClick={() => rotatePhoto('right')} disabled={!!busy}
                          className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs disabled:opacity-50">
                          <RotateCw size={14} /> Rotate right
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="rounded-xl border bg-slate-50 p-4">
                <h3 className="font-semibold">Optional: request a missing portrait or worker introduction</h3>
                <p className="mt-1 text-sm text-slate-600">This is only needed when the staff member has no usable photograph or confirmed introduction. Admin can generate existing-record packs above without requesting re-uploads.</p>
                <label className="mt-3 block text-xs font-medium" htmlFor="client-home">Approved care home / client</label>
                <select id="client-home" value={clientOrgId} onChange={e => setClientOrgId(e.target.value)}
                  className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm">
                  <option value="">Select a current client</option>
                  {clients.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                </select>
                {clients.length === 0 && <p className="mt-2 text-xs text-amber-800">No active CareDock clients available. Create or activate a client in Organisations before requesting an introduction.</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={request} disabled={!!busy || !clients.some(org => org.id === clientOrgId) || current.status === 'pending_review'}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                    <Send size={16} /> Create private invitation
                  </button>
                  <button type="button" onClick={copy} disabled={!inviteLink}
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
                    <ClipboardCopy size={16} /> Copy one-use link
                  </button>
                </div>
                {inviteLink && <p className="mt-3 text-xs text-slate-600">The invitation is ready to copy. It expires {inviteExpires ? new Date(inviteExpires).toLocaleDateString('en-GB') : 'in 7 days'}. For privacy, the secret is shown only immediately after creation, not after leaving this page.</p>}
                <p className="mt-1 text-xs text-slate-500">Send the copied link privately. The worker must sign in to their own CareDock account, then reopen the link to redeem it. Do not send any original compliance files.</p>
              </div>
              {current.summary && (
                <section className="rounded-xl border p-4">
                  <h3 className="font-semibold">2. Review professional introduction</h3>
                  <p className="mt-2 text-xs text-slate-500">Name and job title come from the CareDock staff record. Review the worker's statement against their saved work history before approving; compliance checks are handled separately.</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{current.summary}</p>
                  {current.status === 'pending_review' && (
                    <div className="mt-5 space-y-3">
                      <label className="block text-xs font-semibold" htmlFor="review-note">Correction note (required when requesting changes)</label>
                      <textarea id="review-note" rows={3} maxLength={500} value={note} onChange={e => setNote(e.target.value)}
                        placeholder="Tell the staff member what needs to change"
                        className="w-full rounded-lg border p-3 text-sm" />
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => review('approve')} disabled={!!busy || !current.has_photo}
                          className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                          <CheckCircle2 size={16} /> Approve one-page profile
                        </button>
                        <button onClick={() => review('changes_requested')} disabled={!!busy || !note.trim()}
                          className="rounded-lg border px-4 py-2.5 text-sm disabled:opacity-50">
                          Request corrections
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}
              {current.approved_at && current.status === 'approved' && (
                <section className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <h3 className="font-semibold text-blue-950">3. Approved profile</h3>
                  <p className="mt-1 text-sm text-slate-700">Download a one-page care-home introduction with the CareDock name and job title, approved portrait and summary, and tenant letterhead. The PDF is generated privately here after approval; it is not in the general Documents list. NI, DOB, DBS and Right to Work identifiers are excluded.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={download} disabled={!!busy}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white disabled:opacity-50">
                      <Download size={17} /> Download approved PDF
                    </button>
                    <button onClick={() => review('withdraw')} disabled={!!busy} className="rounded-lg border px-4 py-2.5 text-sm">Withdraw publication</button>
                  </div>
                </section>
              )}
              <p className="text-xs text-slate-500">
                Staff introductions never change onboarding, Right to Work, DBS, qualifications, shift eligibility or payroll.
                Never send the full Employment Pack or evidence vault to a care home.
              </p>
            </div>
          )}
        </article>
      </div>
      </section>
    </PeopleWorkspaceShell>
  );
}
